import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, CircleCheckBig, Clock, Download, FileText, Hash, Printer, Receipt, X } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import { Select } from '../../components/Controls';
import { Kpi } from '../attendance/shared';
import { active, COMMITTED, DetailModal, downloadCsv, groupBy, HBar, Header, MONTHS, naira, REPORT_HEAD, reportCells, REVIEW, Status, StatusPie, sum, TrendBar, TypeBar,
  useMeta } from './shared';
import Loader from '../../components/Loader';

// HR Expenses > Reports · By Department · By Project Site (Stephanet's hrexpenses/Reports.jsx) — the same filters, KPIs and charts, grouped
// by the variant; and Monthly Summary (hrexpenses/Monthly.jsx).
const VARIANT = {
  'hre-reports': { title: 'HR Expense Reports' },
  'hre-by-department': { title: 'Department Expense Summary', by: 'department', chart: 'Amount by Department', label: 'Department' },
  'hre-by-site': { title: 'Project Site Expense Summary', by: 'site', chart: 'Amount by Project Site', label: 'Project Site' },
};
const EMPTY = { department_id: '', type_id: '', branch_id: '', status: '', year: '' };
const PAGE = 50;

export default function HrExpenseReports({ sub }) {
  const v = VARIANT[sub?.key] || VARIANT['hre-reports'];
  const { notify } = useHR();
  const [meta] = useMeta();
  const [f, setF] = useState(EMPTY);
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const load = () => hr.hreList(f).then(r => { setD(r); setError(''); }).catch(e => setError(e.message));
  useEffect(() => { load(); }, [f.department_id, f.type_id, f.branch_id, f.status, f.year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [d]);
  const rows = useMemo(() => (d ? (f.status ? d.rows : active(d.rows)) : []), [d, f.status]);
  const groups = useMemo(() => (v.by ? groupBy(rows, v.by) : []), [rows, v.by]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d || !meta) return <Loader title="Loading reports…" />;
  const set = (k, x) => setF(s => ({ ...s, [k]: x }));
  const approved = rows.filter(r => COMMITTED.includes(r.status));
  const pending = rows.filter(r => REVIEW.includes(r.status));
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const shown = rows.slice((page - 1) * PAGE, page * PAGE);
  const stamp = new Date().toLocaleDateString('en-CA');
  const exportCsv = () => { downloadCsv(`${v.title.replace(/ /g, '_')}_${stamp}.csv`, REPORT_HEAD, rows.map(reportCells)); notify(`${rows.length} records exported`); };
  const exportGroups = () => {
    downloadCsv(`${v.title.replace(/ /g, '_')}_Summary_${stamp}.csv`, [v.label, 'Records', 'Total Amount', 'Approved Amount', 'Pending', 'Share %'],
      groups.map(g => { const gr = rows.filter(r => (r[v.by] || '—') === g.name); return [g.name, g.count, g.value, sum(gr.filter(r => COMMITTED.includes(r.status))), gr.filter(r => REVIEW.includes(r.status)).length, (g.value / (sum(rows) || 1) * 100).toFixed(1)]; }));
    notify('Summary exported');
  };

  return (
    <div className="mdr mds ael hre">
      <Header />
      <div className="mdr-card ar-filters no-print">
        <div className="hre-panel-h"><h3>{v.title}</h3></div>
        <div className="ar-grid hre-filter-grid five">
          <label>Department<Select value={f.department_id} onChange={e => set('department_id', e.target.value)}><option value="">All Depts</option>{meta.departments.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></label>
          <label>Expense Type<Select value={f.type_id} onChange={e => set('type_id', e.target.value)}><option value="">All Types</option>{meta.types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</Select></label>
          <label>Branch<Select value={f.branch_id} onChange={e => set('branch_id', e.target.value)}><option value="">All Branches</option>{meta.branches.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</Select></label>
          <label>Status<Select value={f.status} onChange={e => set('status', e.target.value)}><option value="">All Statuses</option>{meta.statuses.map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label>Year<Select value={f.year} onChange={e => set('year', e.target.value)}><option value="">All Years</option>{meta.years.map(y => <option key={y} value={String(y)}>{y}</option>)}</Select></label>
        </div>
        <div className="ar-actions">
          {d.can.report && <button className="mdr-btn" disabled={!rows.length} onClick={exportCsv}><Download size={14} /> Export CSV</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
          <button className="mdr-btn" onClick={() => setF(EMPTY)}><X size={14} /> Clear</button>
          <span className="hre-summary inline"><b>{rows.length} records</b> · {naira(sum(rows))} · Approved: {naira(sum(approved))}</span>
        </div>
      </div>

      <div className="md-kpis mds-kpis hre-kpis">
        <Kpi label="Total Records" value={rows.length} Ico={FileText} tone="blue" note={f.status || 'All statuses (active)'} />
        <Kpi label="Total Amount" value={naira(sum(rows))} Ico={Receipt} tone="indigo" note="Combined expense" i={1} />
        <Kpi label="Approved Amount" value={naira(sum(approved))} Ico={CircleCheckBig} tone="green" note={`${approved.length} approved`} i={2} />
        <Kpi label="In Review" value={naira(sum(pending))} Ico={Clock} tone="amber" note={`${pending.length} awaiting approval`} i={3} />
      </div>

      <div className="hre-grid2">
        {v.by && <HBar rows={rows} by={v.by} title={v.chart} top={10} />}
        <TypeBar rows={rows} title="Expense by Type (Top 8)" />
        <TrendBar rows={rows} year={f.year} title="Monthly Trend" />
        <StatusPie rows={rows} />
      </div>

      {v.by && <div className="mdr-card mdr-table-card">
        <div className="hre-panel-h hre-pad"><h3>{v.label} Summary ({groups.length})</h3>{d.can.report && <button className="mdr-btn" disabled={!groups.length} onClick={exportGroups}><Download size={13} /> Export</button>}</div>
        <div className="mdr-scroll"><table className="mdr-table mds-table hre-table">
          <thead><tr><th>{v.label}</th><th className="r">Records</th><th className="r">Total Amount</th><th className="r">Approved</th><th className="r">Pending</th><th>Share</th></tr></thead>
          <tbody>{groups.map(g => {
            const gr = rows.filter(r => (r[v.by] || '—') === g.name);
            const pct = g.value / (sum(rows) || 1) * 100;
            return <tr key={g.name}><td><b>{g.name}</b></td><td className="r">{g.count}</td><td className="r mono">{naira(g.value)}</td>
              <td className="r mono">{naira(sum(gr.filter(r => COMMITTED.includes(r.status))))}</td>
              <td className="r">{gr.filter(r => REVIEW.includes(r.status)).length}</td>
              <td><div className="hre-share"><i style={{ width: `${pct}%` }} /><span>{pct.toFixed(1)}%</span></div></td></tr>;
          })}</tbody>
          {groups.length > 0 && <tfoot><tr><td><b>Total</b></td><td className="r"><b>{rows.length}</b></td><td className="r mono"><b>{naira(sum(rows))}</b></td><td className="r mono"><b>{naira(sum(approved))}</b></td><td /><td /></tr></tfoot>}
        </table></div>
      </div>}

      <div className="mdr-card mdr-table-card">
        <div className="hre-panel-h hre-pad"><h3>{v.title} ({rows.length} records)</h3>{d.can.report && <button className="mdr-btn" disabled={!rows.length} onClick={exportCsv}><Download size={13} /> Export</button>}</div>
        <div className="mdr-scroll"><table className="mdr-table mds-table hre-table">
          <thead><tr>{REPORT_HEAD.map(h => <th key={h} className={h === 'Amount' ? 'r' : ''}>{h}</th>)}</tr></thead>
          <tbody>{shown.map((r, i) => (
            <tr key={r.id} className="mdr-row clickable" onClick={() => setViewing(r.id)}>
              <td>{(page - 1) * PAGE + i + 1}</td><td className="mono nowrap"><b>{r.reference}</b></td><td className="mono small">{r.ref_no}</td><td className="nowrap">{r.employee || '—'}</td>
              <td className="nowrap">{r.department || '—'}</td><td className="nowrap">{r.branch || '—'}</td><td className="nowrap">{r.site || '—'}</td><td className="nowrap">{r.title}</td>
              <td className="nowrap">{r.expense_type || '—'}</td><td className="r mono nowrap">{naira(r.amount)}</td><td><Status s={r.status} /></td><td>{r.payment}</td>
              <td className="nowrap small">{r.payroll_impact}</td><td>{r.year}</td><td>{r.month_name}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={15} className="mdr-empty">No records for the selected filters.</td></tr>}</tbody>
        </table></div>
        {pages > 1 && <div className="ael-pager"><span>Page {page} of {pages}</span><div><button className="mdr-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
          <button className="mdr-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button></div></div>}
      </div>
      {viewing && <DetailModal id={viewing} notify={notify} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
}

// ── Monthly Summary: one month's expenses by department and by type ──
const lastMonths = (n = 12) => {
  const now = new Date();
  return Array.from({ length: n }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); return { y: d.getFullYear(), m: d.getMonth() + 1 }; });
};

function Group({ title, rows, by, other }) {
  const map = new Map();
  rows.forEach(r => { const k = r[by] || '—'; const g = map.get(k) || { name: k, rows: [] }; g.rows.push(r); map.set(k, g); });
  const list = [...map.values()].map(g => ({ ...g, total: sum(g.rows) })).sort((a, b) => b.total - a.total);
  return (
    <div className="mdr-card hre-sec">
      <h3>{title}</h3>
      {list.length ? <div className="hre-mcards">{list.map(g => {
        const parts = new Map();
        g.rows.forEach(r => parts.set(r[other] || '—', (parts.get(r[other] || '—') || 0) + r.amount));
        return (
          <div key={g.name} className="hre-mcard">
            <div className="hre-mcard-h"><b>{g.name}</b><span>{g.rows.length} txn</span></div>
            <small>Total Amount</small><strong>{naira(g.total)}</strong>
            <ul>{[...parts.entries()].sort((a, b) => b[1] - a[1]).map(([k, x]) => <li key={k}><span>{k}</span><b>{naira(x)}</b></li>)}</ul>
          </div>
        );
      })}</div> : <div className="hre-empty">No expenses in this month.</div>}
    </div>
  );
}

export function HrExpenseMonthly() {
  const months = useMemo(() => lastMonths(12), []);
  const [pick, setPick] = useState(`${months[0].y}-${months[0].m}`);
  const [empOnly, setEmpOnly] = useState(false);
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [y, m] = pick.split('-').map(Number);
  useEffect(() => { hr.hreList({ year: y }).then(setD).catch(e => setError(e.message)); }, [y]);
  if (error) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading…" />;
  const rows = active(d.rows).filter(r => r.month === m && r.status !== 'Rejected' && (!empOnly || r.employee_id || r.employee_count));
  const total = sum(rows);
  return (
    <div className="mdr mds ael hre">
      <Header title="Monthly Summary" sub="Aggregate view of expenses by department and type" />
      <div className="hre-bar no-print">
        <Select value={pick} onChange={e => setPick(e.target.value)}>{months.map(x => <option key={`${x.y}-${x.m}`} value={`${x.y}-${x.m}`}>{new Date(x.y, x.m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</option>)}</Select>
        <label className="hre-check"><input type="checkbox" checked={empOnly} onChange={e => setEmpOnly(e.target.checked)} /> Employee Only</label>
        <span className="muted small">Excludes rejected, cancelled and archived expenses.</span>
      </div>
      <div className="md-kpis mds-kpis hre-kpis three">
        <Kpi label="Total Transactions" value={rows.length} Ico={Hash} tone="blue" note={`${MONTHS[m - 1]} ${y}`} />
        <Kpi label="Total Amount" value={naira(total)} Ico={Receipt} tone="green" note={empOnly ? 'Employee-linked only' : 'All allocations'} i={1} />
        <Kpi label="Avg per Transaction" value={naira(rows.length ? total / rows.length : 0)} Ico={Calculator} tone="purple" note="Total ÷ transactions" i={2} />
      </div>
      <Group title="By Department" rows={rows} by="department" other="expense_type" />
      <Group title="By Expense Type" rows={rows} by="expense_type" other="department" />
    </div>
  );
}
